import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import {
  HTTP_STATUS,
  authConstants,
  campaignConstants,
} from "@/config/constants";

type CampaignPayload = Record<string, any>;

type CampaignState = {
  campaignReport: unknown;
  error: any;
  isSaved: boolean;
  is_join_camp_tnc_checked: boolean;
  loading: boolean;
};

type RootState = {
  campaign: CampaignState;
};

export const joinCampaignThunk = createAsyncThunk(
  "campaign/joinCampaign",
  async (payload: CampaignPayload, { rejectWithValue }) => {
    try {
      const res = await campaignService.joinCampaign(payload);
      if (res.status === HTTP_STATUS.CREATED && res.data)
        return {
          data: res.data,
          is_join_camp_tnc_checked: payload.is_join_camp_tnc_checked,
        };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const joinPostCardCampaignThunk = createAsyncThunk(
  "campaign/joinPostCardCampaign",
  async (payload: CampaignPayload, { rejectWithValue }) => {
    try {
      const res = await campaignService.joinPostCardCampaign(payload);
      if (res.status === HTTP_STATUS.CREATED && res.data)
        return {
          data: res.data,
          is_join_camp_tnc_checked: payload.is_join_camp_tnc_checked,
        };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const getCampaignReportThunk = createAsyncThunk(
  "campaign/getCampaignReport",
  async (payload: CampaignPayload, { rejectWithValue }) => {
    try {
      const res = await campaignService.getCampaignReport(payload);
      if (res.status === HTTP_STATUS.OK && res.data) return res.data;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

const initialState: CampaignState = {
  error: null,
  loading: false,
  isSaved: false,
  campaignReport: null,
  is_join_camp_tnc_checked: false,
};

const campaignSlice = createSlice({
  name: "campaign",
  initialState,
  reducers: {
    resetIsSavedDraftSuccess(state) {
      state.isSaved = false;
    },
    resetError(state) {
      state.error = null;
    },
    setJoinCampTnCChecked(state, action: PayloadAction<boolean>) {
      state.is_join_camp_tnc_checked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(joinCampaignThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isSaved = false;
      })
      .addCase(joinCampaignThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isSaved = true;
        if (action.payload?.is_join_camp_tnc_checked)
          state.is_join_camp_tnc_checked =
            action.payload.is_join_camp_tnc_checked;
      })
      .addCase(joinCampaignThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isSaved = false;
      })
      .addCase(joinPostCardCampaignThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isSaved = false;
      })
      .addCase(joinPostCardCampaignThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isSaved = true;
        if (action.payload?.is_join_camp_tnc_checked)
          state.is_join_camp_tnc_checked =
            action.payload.is_join_camp_tnc_checked;
      })
      .addCase(joinPostCardCampaignThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isSaved = false;
      })
      .addCase(getCampaignReportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.campaignReport = null;
      })
      .addCase(getCampaignReportThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.campaignReport = action.payload;
      })
      .addCase(getCampaignReportThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Legacy action types for backward compatibility
      .addCase(campaignConstants.SAVE_DRAFT_SUCCESS, (state) => {
        state.loading = false;
        state.isSaved = true;
      })
      .addCase(
        campaignConstants.SAVE_DRAFT_FAIL,
        (state, action: PayloadAction<unknown>) => {
          state.loading = false;
          state.error = action.payload;
          state.isSaved = false;
        }
      )
      .addCase(
        campaignConstants.SET_JOIN_CAMP_TNC_CHECKED,
        (state, action: PayloadAction<boolean>) => {
          state.is_join_camp_tnc_checked = action.payload;
        }
      )
      .addCase(authConstants.LOGOUT, () => initialState);
  },
});

export const { resetIsSavedDraftSuccess, resetError, setJoinCampTnCChecked } =
  campaignSlice.actions;
export default campaignSlice.reducer;

// Selectors
export const campaignIsLoadingSelector = (state: RootState) =>
  state.campaign.loading;
export const isSavedSelector = (state: RootState) => state.campaign.isSaved;
export const campaignReportSelector = (state: RootState) =>
  state.campaign.campaignReport;
export const campaignErrorSelector = (state: RootState) => state.campaign.error;
export const isJoinCampTnCCheckedSelector = (state: RootState) =>
  state.campaign.is_join_camp_tnc_checked;
